import { Box, Stack } from '@chakra-ui/react'
import { uniqBy } from 'lodash-es'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useState } from 'react'

import type { SerializedRichText } from '@/client/services/richText/types'

import LiveHelpReplyGuidelines from '@/components/Community/Channel/LiveHelpReplyGuidelines'
import LiveHelpBanner from '@/components/Community/LiveHelp/LiveHelpBanner'
import PostTypingStatus from '@/components/Community/Post/PostTypingStatus'
import type {
  PostDetails,
  PostEventDetails,
  PostFeedbackDetails,
} from '@/components/Community/Post/types'
import NewComment from '@/components/Community/PostEvent/NewComment'
import PostEventList from '@/components/Community/PostEvent/PostEventList'
import ReportModalV2 from '@/components/Report/ReportModalV2'
import type { MentionsProps, UserMention } from '@/components/UI/RichTextEditor/types'
import type { FileUpload } from '@/components/Worlds/FileUpload/types'
import type { UserDetails } from '@/components/Worlds/types'
import Container from '@/components/Worlds/UI/Container/Container'

import DeletePostModal from './internal/DeletePostModal'
import EditPostModal from './internal/EditPostModal'
import PostFeedbackBanner from './internal/PostFeedbackBanner'
import PostFeedbackForm from './internal/PostFeedbackForm'
import PostReplyCount from './internal/PostReplyCount'
import PostContents from './PostContents'

function getUniqueUsers(
  postEvents: PostEventDetails[],
  postUser: PostDetails['user']
): UserMention[] {
  return uniqBy(postEvents.map(event => event.user).concat(postUser), 'id').map(user => ({
    id: user.id,
    displayName: user.displayName,
    avatarUrl: user.avatarUrl,
    username: user.username,
  }))
}

export default function PostFull({
  post,
  postEvents,
  postFeedbacks,
  postIsFullView,
  currentUser,
}: {
  post: PostDetails
  postEvents: PostEventDetails[]
  postFeedbacks: PostFeedbackDetails[]
  postIsFullView: boolean
  currentUser: UserDetails | null
}): JSX.Element {
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)

  const [postEventsInternal, setPostEventsInternal] = useState<PostEventDetails[]>(postEvents)
  const [optimisticPostEvents, setOptimisticPostEvents] = useState<
    (PostEventDetails & { postId: number })[]
  >([])

  useEffect(() => setPostEventsInternal(postEvents), [postEvents])

  const { body, id, channel } = post
  const { liveHelpInfo } = post

  const onCommentSubmit = useCallback(
    ({
      body: newCommentBody,
      attachments,
      isCollapsible,
      clientId,
    }: {
      body: SerializedRichText
      attachments: FileUpload[]
      isCollapsible: boolean
      clientId: string
    }) => {
      if (currentUser == null) {
        return
      }

      const now = DateTime.now()
      setOptimisticPostEvents(events => [
        ...events,
        {
          postId: post.id,
          id: 0,
          type: 'comment',
          attachments,
          createdAt: now,
          updatedAt: now,
          body: newCommentBody,
          payload: null,
          isCollapsible,
          clientId,
          user: currentUser,
          reactions: [],
        },
      ])
    },
    [post.id, currentUser]
  )

  const onSubmitFailed = useCallback((clientId: string) => {
    setOptimisticPostEvents(events => events.filter(event => event.clientId !== clientId))
  }, [])

  const onPostEventDelete = useCallback((clientId: string) => {
    setPostEventsInternal(events => events.filter(event => event.clientId !== clientId))
    setOptimisticPostEvents(events => events.filter(event => event.clientId !== clientId))
  }, [])

  const onAttachmentUploadComplete = useCallback(
    ({ clientId, src }: { clientId: string; src: string }) => {
      setOptimisticPostEvents(events =>
        events.map(event => {
          if (event.clientId !== clientId) {
            return event
          }
          const newAttachments = event.attachments.map(attachment =>
            attachment.src === src ? { ...attachment, loading: false } : attachment
          )
          return { ...event, attachments: newAttachments }
        })
      )
    },
    []
  )

  const mentions: MentionsProps = {
    usersInThread: getUniqueUsers(postEventsInternal, post.user),
  }

  return (
    <Container px={0} pt={0} pb={0} maxWidth="100%">
      <EditPostModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onDiscard={() => setShowEditModal(false)}
        postId={id}
        postUser={post.user}
        channel={channel}
        initialBody={body}
        initialShouldRequestLiveHelp={liveHelpInfo?.currentLiveHelpRequest != null}
      />
      <DeletePostModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        postId={id}
      />
      <ReportModalV2
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        entity={{ type: 'post', id }}
        reportedUserName={post.user.displayName}
      />

      <Stack direction="column" spacing="24px" px="24px" my={0}>
        <LiveHelpBanner post={post} mt="24px" mb="24px" postIsFullView={postIsFullView} />
        <PostFeedbackBanner postFeedbacks={postFeedbacks} />
      </Stack>

      <Box p="24px" _hover={{ bg: 'bg.subtle' }}>
        <PostContents
          post={post}
          onEdit={() => setShowEditModal(true)}
          onDelete={() => setShowDeleteModal(true)}
          onReport={() => setShowReportModal(true)}
          showPostStatusSection
          showCommentButton
          showMenuOnGroupHover
          hideFullViewMenuItem={postIsFullView}
        />
      </Box>

      {liveHelpInfo?.postStatus === 'solved' &&
      currentUser != null &&
      currentUser.id === post.user.id ? (
        <PostFeedbackForm
          post={post}
          postEvents={postEventsInternal}
          postFeedbacks={postFeedbacks}
          p="24px"
        />
      ) : null}

      <Box p="24px">
        <PostReplyCount countReplies={post.countReplies} />
      </Box>

      <Stack direction="column" spacing="16px">
        <PostEventList
          post={post}
          postEvents={postEventsInternal}
          onPostEventDelete={onPostEventDelete}
          optimisticPostEvents={optimisticPostEvents.filter(op => op.postId === post.id)}
        />
        {post?.user.id !== currentUser?.id &&
        channel.type === 'question' &&
        liveHelpInfo?.worldDetails != null ? (
          <Box px="24px" mt="12px">
            <LiveHelpReplyGuidelines
              postEvents={postEventsInternal}
              post={post}
              worldSlug={liveHelpInfo.worldDetails.slug}
            />
          </Box>
        ) : null}
        <Box px="24px" pb="24px">
          <PostTypingStatus postId={post.id} postEvents={postEvents} />
          <NewComment
            // Ensures that changing the postId clears the comment box
            key={post.id}
            postId={post.id}
            onSubmit={onCommentSubmit}
            onSubmitFailed={onSubmitFailed}
            onAttachmentUploadComplete={onAttachmentUploadComplete}
            mentions={mentions}
          />
        </Box>
      </Stack>
    </Container>
  )
}
